import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import SelectCell from "./selectCell";
import Select from "react-select";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import "./index.css";
import { getPost } from "../utils/commHelper";
import { getUrlWithFilters, createFilterCondition, createEmptyFilterGroup } from "./filterUtils";

const Cell = ({
  cell,
  type,
  onCellChange,
  width,
  refItems,
  refColumn,
  readOnly,
  refRes,
  style,
  isFilter,
  multiline,
  apiTarget,
  isMultiRef = false,
}) => {
  let initValue = useRef(cell);

  const [value, setValue] = useState(initValue.current);

  // Update value when cell prop changes (e.g., after save)
  React.useEffect(() => {
    if (cell !== initValue.current) {
      initValue.current = cell;
      setValue(cell);
    }
  }, [cell]);

  const _renderCell = () => {
    // Handle multi-reference arrays
    if (isMultiRef && Array.isArray(value)) {
      return (
        <MultiRefCell
          value={value}
          readOnly={readOnly}
          onCellChange={onCellChange}
          refColumn={refColumn}
          refRes={refRes}
          apiTarget={apiTarget}
        />
      );
    }

    switch (type) {
      case "column":
        return <Column readOnly={readOnly} value={value} />;
      case "Boolean":
        return (
          <Boolean
            readOnly={readOnly}
            value={value}
            onChange={(newValue) => {
              if (!readOnly) {
                setValue(newValue);
                onCellChange && onCellChange(newValue);
              }
            }}
          />
        );
      case "reference":
        return (
          <SelectCell
            isFilter={isFilter}
            width={width}
            refRes={refRes}
            readOnly={readOnly}
            value={value}
            onCellChange={onCellChange}
            refItems={refItems}
            refColumn={refColumn}
            apiTarget={apiTarget}
          />
        );
      case "Date":
      case "timestamp":
        return (
          <TimestampCell
            onCellChange={onCellChange}
            readOnly={readOnly}
            value={value}
          />
        );
      default:
        return (
          <TextCell
            type={type}
            readOnly={readOnly}
            value={value}
            setValue={setValue}
            multiline={multiline}
            onFocus={() => {
              initValue.current = value;
            }}
            onBlur={async () => {
              if (initValue.current !== value) {
                onCellChange && onCellChange(value);
              }
            }}
          />
        );
    }
  };

  return (
    <div
      style={{
        minHeight: "42px",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        backgroundColor: "transparent",
        ...style,
      }}
    >
      {_renderCell()}
    </div>
  );
};
export default Cell;

// Multi-reference cell with expandable view and editing
const MultiRefCell = ({ value, readOnly, onCellChange, refColumn, refRes, apiTarget }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [options, setOptions] = useState([]);
  const [loader, setLoader] = useState(false);
  const containerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const currentValue = value || [];
  const displayCount = currentValue.length;
  const previewItems = currentValue.slice(0, 3);
  
  // Calculate dropdown position when expanded
  useEffect(() => {
    if (expanded && containerRef.current) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width
          });
        }
      };
      
      updatePosition();
      
      // Update position on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [expanded]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    if (expanded) {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          // Check if click is on the portal dropdown
          const dropdown = document.querySelector('[data-multiref-dropdown]');
          if (!dropdown || !dropdown.contains(event.target)) {
            setExpanded(false);
          }
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [expanded]);

  // Load reference options
  const getDataFromServer = async () => {
    if (refRes.current[refColumn]) {
      createOptions(refRes.current[refColumn]);
    } else {
      const res = await getPost(
        "/getInterfaceTitlesRef",
        {
          tableName: refColumn,
        },
        apiTarget
      ).then((response) => response.data);
      refRes.current = { ...refRes.current, [refColumn]: res };
      createOptions(res);
    }
  };

  const getFilteredDataFromServer = async (searchVal) => {
    const res = await getPost(
      "/getInterfaceFilteredData",
      {
        tableName: refColumn,
        keys: searchVal,
      },
      apiTarget
    ).then((response) => response.data);
    refRes.current = { ...refRes.current, [refColumn]: res };
    createOptions(res);
  };

  const createOptions = (items) => {
    const currentIds = new Set(currentValue.map(v => v._id || v));
    const newOptions = items
      .filter(item => !currentIds.has(item._id || item))
      .map(item => ({
        value: item._id || item,
        label: item.title || item._id || item
      }));
    setOptions(newOptions);
  };

  const handleAddItems = (selectedOptions) => {
    if (!onCellChange || readOnly) return;
    
    const newItems = selectedOptions.map(opt => ({
      _id: opt.value,
      title: opt.label || opt.value
    }));
    
    const updatedValue = [...currentValue, ...newItems];
    onCellChange(updatedValue);
    setShowAddDropdown(false);
  };

  const handleRemoveItem = (itemId) => {
    if (!onCellChange || readOnly) return;
    
    const updatedValue = currentValue.filter(item => (item._id || item) !== itemId);
    onCellChange(updatedValue);
  };

  const handleItemClick = (item) => {
    if (!refColumn) return;
    
    // Create a filter for this item's _id
    const itemId = typeof item === 'object' && item !== null ? (item._id || item) : item;
    if (!itemId) return;
    
    // Create filter condition: _id equals itemId
    const filterCondition = createFilterCondition('_id', 'equals', itemId);
    const filterGroup = createEmptyFilterGroup('AND');
    filterGroup.conditions = [filterCondition];
    
    // Navigate to the table with the filter
    const url = getUrlWithFilters(refColumn, filterGroup);
    window.location.href = url;
  };

  const handleToggleExpanded = () => {
    if (readOnly) {
      setExpanded(!expanded);
    } else {
      setExpanded(!expanded);
      if (!expanded && !showAddDropdown) {
        setLoader(true);
        getDataFromServer().then(() => {
          setLoader(false);
        });
      }
    }
  };

  if (currentValue.length === 0 && readOnly) {
    return (
      <div style={{ padding: "8px", color: "#999" }}>
        No items
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} style={{ width: "100%", position: "relative" }}>
        <div
          onClick={handleToggleExpanded}
          style={{
            padding: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              backgroundColor: "#e3f2fd",
              color: "#1976d2",
              padding: "2px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {displayCount} items
          </span>
          {displayCount > 0 && (
            <span style={{ fontSize: "13px", color: "#666" }}>
              {previewItems.map((item) => {
                if (typeof item === 'object' && item !== null) {
                  return item.title || item._id || JSON.stringify(item);
                }
                return String(item);
              }).join(", ")}
              {displayCount > 3 && "..."}
            </span>
          )}
        </div>
        <span style={{ color: "#1976d2", fontSize: "12px" }}>
          {expanded ? "▲" : "▼"}
        </span>
        </div>
      </div>

      {/* Expandable dropdown - rendered as portal to avoid stacking context issues */}
      {expanded && createPortal(
        <div
          data-multiref-dropdown
          style={{
            position: "fixed",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 10000,
            maxHeight: "400px",
            overflowY: "auto",
            opacity: 1,
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: "bold" }}>{displayCount} Referenced Items</span>
            {!readOnly && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddDropdown(!showAddDropdown);
                  if (!showAddDropdown) {
                    setLoader(true);
                    getDataFromServer().then(() => {
                      setLoader(false);
                    });
                  }
                }}
                style={{
                  padding: "4px 8px",
                  fontSize: "12px",
                  backgroundColor: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                + Add Items
              </button>
            )}
          </div>

          {/* Add items dropdown */}
          {showAddDropdown && !readOnly && (
            <div style={{ padding: "8px", borderBottom: "1px solid #eee", backgroundColor: "#f9f9f9" }}>
              <Select
                isMulti
                options={options}
                onChange={handleAddItems}
                onInputChange={(inputValue) => {
                  if (inputValue && inputValue.length > 0) {
                    setLoader(true);
                    getFilteredDataFromServer(inputValue).then(() => {
                      setLoader(false);
                    });
                  } else {
                    // If search is cleared, load all options
                    setLoader(true);
                    getDataFromServer().then(() => {
                      setLoader(false);
                    });
                  }
                }}
                placeholder="Search and select items..."
                menuPortalTarget={document.body}
                isLoading={loader}
                isSearchable={true}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 10001,
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 10001,
                  }),
                }}
              />
            </div>
          )}

          {/* List of items */}
          {currentValue.length > 0 ? (
            currentValue.map((item, index) => (
              <div
                key={item._id || index}
                style={{
                  padding: "8px 12px",
                  borderBottom: index < currentValue.length - 1 ? "1px solid #f0f0f0" : "none",
                  fontSize: "13px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleItemClick(item);
                  }}
                  style={{
                    cursor: "pointer",
                    flex: 1,
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f7ff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  title="Click to view this item in its table"
                >
                  <div style={{ fontWeight: "500", color: "#1976d2" }}>
                    {typeof item === 'object' && item !== null 
                      ? (item.title || item._id || JSON.stringify(item))
                      : String(item)
                    }
                  </div>
                  {typeof item === 'object' && item !== null && item._id && (
                    <div style={{ fontSize: "11px", color: "#999" }}>{item._id}</div>
                  )}
                </div>
                {!readOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(item._id || item);
                    }}
                    style={{
                      padding: "4px 8px",
                      fontSize: "11px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))
          ) : (
            !readOnly && (
              <div style={{ padding: "12px", textAlign: "center", color: "#999" }}>
                No items. Click "Add Items" to add references.
              </div>
            )
          )}
        </div>,
        document.body
      )}
    </>
  );
};

const TextCell = ({
  value,
  setValue,
  onBlur,
  onFocus,
  readOnly,
  type,
  multiline,
}) => {
  // JSON / object values would otherwise render as "[object Object]". Show
  // readable JSON in a textarea and parse edits back so saving keeps the shape.
  const isObjectValue = value !== null && typeof value === "object";
  const [jsonText, setJsonText] = useState(() =>
    isObjectValue ? JSON.stringify(value, null, 2) : ""
  );

  const floatRegExp = new RegExp("^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$");
  const pattern =
    type === "Number" ? "[0-9]*" : type === "real" ? floatRegExp : null;

  if (type === "real") {
    return (
      <input
        type="number"
        style={{
          width: "100%",
          height: "100%",
          padding: "8px",
          whiteSpace: "pre-wrap",
          border: "0 solid transparent",
          borderRadius: 0,
          backgroundColor: "transparent",
        }}
        className="form-control"
        readOnly={readOnly}
        onBlur={onBlur}
        onFocus={onFocus}
        min="0.00"
        step="0.001"
        value={value}
        onChange={(event) => {
          event.target.validity.valid && setValue(event.target.value);
        }}
      />
    );
  }
  const style = {
    width: "100%",
    height: "100%",
    padding: "8px",
    whiteSpace: "pre-wrap",
    border: "0 solid transparent",
    borderRadius: 0,
    backgroundColor: "transparent",
  };

  const onChange = (event) => {
    event.target.validity.valid && setValue(event.target.value);
  };

  if (isObjectValue) {
    return (
      <textarea
        readOnly={readOnly}
        value={jsonText}
        onBlur={onBlur}
        onFocus={onFocus}
        style={{ ...style, fontFamily: "monospace", minHeight: "80px" }}
        onChange={(event) => {
          const text = event.target.value;
          setJsonText(text);
          if (text.trim() === "") {
            setValue(null);
            return;
          }
          try {
            // Only propagate when it parses to valid JSON, so partial/invalid
            // edits don't corrupt the stored value.
            setValue(JSON.parse(text));
          } catch {
            // keep last valid value until the text is valid JSON again
          }
        }}
      />
    );
  }

  if (multiline) {
    return (
      <textarea
        readOnly={readOnly}
        value={value || ""}
        onBlur={onBlur}
        onFocus={onFocus}
        style={style}
        onChange={onChange}
      />
    );
  }

  return (
    <input
      type={"text"}
      pattern={pattern}
      readOnly={readOnly}
      value={value || ""}
      onBlur={onBlur}
      onFocus={onFocus}
      style={style}
      onChange={onChange}
    />
  );
};

const TimestampCell = ({ value, onCellChange, readOnly }) => {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        pointerEvents: readOnly ? "none" : "auto",
        backgroundColor: "transparent",
        overflow: "initial",
      }}
    >
      <Datetime
        inputProps={{ className: "datetime", disabled: true }}
        // open={false}
        renderInput={(props, openCalendar, closeCalendar) =>
          renderInput(props, openCalendar, closeCalendar, readOnly)
        }
        closeOnSelect
        initialValue={value ? new Date(value) : null}
        onChange={(newValue) => {
          onCellChange && onCellChange(newValue ? new Date(newValue) : null);
        }}
      />
    </div>
  );
};

const renderInput = (props, openCalendar, closeCalendar, readOnly) => {
  function clear() {
    props.onChange({ target: { value: "" } });
  }

  return (
    <div onClick={!readOnly && openCalendar}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          padding: "0 20px",
        }}
      >
        <input
          style={{
            borderRadius: "4px",
            padding: "5px",
            border: "0",
            boxShadow: readOnly ? null : "1px 1px 6px #dadada",
            color: "black",
          }}
          {...props}
        />
        {/*<button style={{*/}
        {/*    backgroundColor: 'transparent',*/}
        {/*}} onClick={openCalendar}>open calendar</button>*/}
        {/*<button onClick={closeCalendar}>close calendar</button>*/}
        {!readOnly && props.value !== "" && (
          <button
            style={{
              marginLeft: "20px",
              padding: "2px 10px",
              borderRadius: "4px",
              border: "0",
              boxShadow: "1px 1px 6px #cacaca",
              backgroundColor: "#9a9a9a",
              color: "white",
            }}
            onClick={clear}
          >
            clear
          </button>
        )}
      </div>
    </div>
  );
};

const Column = ({ value }) => {
  return (
    <div
      style={{
        backgroundColor: "transparent",
        width: "100%",
        height: "100%",
        padding: "10px",
        whiteSpace: "pre-wrap",
        cursor: "pointer",
      }}
    >
      {value}
    </div>
  );
};
const Boolean = ({ value, onChange, readOnly = false }) => {
  return (
    <input
      type={"checkbox"}
      checked={value || false}
      disabled={readOnly}
      style={{
        backgroundColor: "transparent",
        width: "100%",
        cursor: readOnly ? "not-allowed" : "pointer",
        // No extra dimming when read-only: the browser already greys a disabled checkbox,
        // and fading it further made it hard to tell ticked from unticked.
      }}
      onChange={(event) => {
        if (!readOnly && onChange) {
          onChange(event.target.checked);
        }
      }}
    />
  );
};
